import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { v4 as uuid } from "uuid";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Layout } from "@lookiero/sty-psp-ui";
import { Product } from "../../../../domain/entities/product/Product";
import { Price } from "../../../../domain/projections/Price";
import { useAddProductToBasket } from "../../behaviors/useAddProductToBasket";
import { useGetCurrencyAndProductVariants } from "../../behaviors/useGetCurrencyAndProductVariants";
import { useGetOrder } from "../../behaviors/useGetOrder";
import { useRemoveVariantFromWishlist } from "../../behaviors/useRemoveVariantFromWishlist";
import { Wishlist as WishlistTemplate } from "../../components/templates/wishlist/Wishlist";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { useToast } from "../../hooks/useToast";
import { DOMAIN, I18nMessages } from "../../i18n/i18n";
import { Routes } from "../../routing/routes";
import { TrackingPage, TrackingView } from "../../tracking/tracking";
import { useTrackBasketItemAdded } from "../../tracking/useTrackBasketItemAdded";
import { useTrackPageView } from "../../tracking/useTrackPageView";
import { useTrackViewWishlist } from "../../tracking/useTrackViewWishlist";
import { useTrackWhishlistItemRemoved } from "../../tracking/useTrackWhishlistItemRemoved";

const WISHLIST_ANALYTICS = "Shop Wishlist";

interface WishlistProps {
  readonly layout: Layout;
}

const Wishlist: FC<WishlistProps> = ({ layout }) => {
  const {
    session: { auth, user },
    basePath,
    tradename,
    apiUrl,
  } = useStaticInfo();

  const whishlistNotificationItemRemovedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.WISHLIST_NOTIFICATION_ITEM_REMOVED,
  });
  const productSizeSelectorItemAddedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_SIZE_SELECTOR_ITEM_ADDED,
  });

  const trackingCommonParams = useMemo(
    () => ({
      country: user.country,
      segment: user.segment,
      userId: user.id,
      tradename,
    }),
    [tradename, user.country, user.id, user.segment],
  );
  useTrackPageView({
    ...trackingCommonParams,
    page: TrackingPage.WISHLIST,
    view: TrackingView.LOOK_IN_WISHLIST,
  });
  const trackBasketItemAdded = useTrackBasketItemAdded(trackingCommonParams);
  const trackViewWishlist = useTrackViewWishlist(trackingCommonParams);
  const trackWhishlistItemRemoved = useTrackWhishlistItemRemoved(trackingCommonParams);

  const { order } = useGetOrder();
  const { productVariants, isLoading, refetch: getWishlistElements } = useGetCurrencyAndProductVariants();
  const addProductToBasket = useAddProductToBasket();
  const removeVariantFromWishlist = useRemoveVariantFromWishlist();

  const navigate = useNavigate();
  const handleOnPressCart = useCallback(() => navigate(`${basePath}/${Routes.CART}`), [basePath, navigate]);

  const toast = useToast();

  useEffect(() => {
    if (productVariants && !isLoading) {
      trackViewWishlist({
        products: productVariants,
      });
    }
  }, [user, productVariants, isLoading, trackViewWishlist]);

  const removeVariantFromWishlistOnSuccess = useCallback(
    async (variantId: string) => {
      const product = await productVariants.find(({ variantId: productVariantId }) => productVariantId === variantId);

      if (product) {
        trackWhishlistItemRemoved({
          product: {
            title: product.title,
            variantId: product.variantId,
            price: product.price as unknown as Price,
            brand: product.brand,
            size: product.size,
          } as Product,
          list: WISHLIST_ANALYTICS,
        });
      }
      getWishlistElements();

      toast.showSuccess({ message: whishlistNotificationItemRemovedText });
    },
    [productVariants, getWishlistElements, toast, whishlistNotificationItemRemovedText, trackWhishlistItemRemoved],
  );

  const addProductToBasketOnSuccess = useCallback(
    async (variantId: string) => {
      const product = await productVariants.find(({ variantId: productVariantId }) => productVariantId === variantId);

      if (product) {
        trackBasketItemAdded({
          product: {
            title: product.title,
            variantId: product.variantId,
            price: product.price as unknown as Price,
            brand: product.brand,
            size: product.size,
          } as Product,
          currency: order.currency,
        });
      }

      toast.showSuccess({ message: productSizeSelectorItemAddedText });
    },
    [productVariants, toast, productSizeSelectorItemAddedText, trackBasketItemAdded, order.currency],
  );

  const handleOnDeleteFromWishlist = async (variantId: string) => {
    removeVariantFromWishlist(
      variantId,
      () => removeVariantFromWishlistOnSuccess(variantId),
      (error) => console.error(error),
    );
  };

  const handleOnAddToBasket = async (variantId: string) => {
    const { orderId, serviceId } = order;
    const itemId = uuid();

    addProductToBasket(
      { auth, itemId, orderId, variantId, serviceId, apiUrl },
      () => addProductToBasketOnSuccess(variantId),
      (error) => console.error(error),
    );
  };

  const handleOnBack = useCallback(() => navigate(-1), [navigate]);

  return (
    <WishlistTemplate
      hasBasketNotification={order.items.length > 0}
      isLoading={isLoading}
      layout={layout}
      productVariants={productVariants}
      onAddToBasket={handleOnAddToBasket}
      onBack={handleOnBack}
      onDeleteFromWishlist={handleOnDeleteFromWishlist}
      onPressCart={handleOnPressCart}
    />
  );
};

export { Wishlist };
